import React, { createContext, useContext } from 'react';

import type { IconName } from '../components/element/Icon/icon.types';
import { IconNames } from '../components/element/Icon/icon.types';

/**
 * Which glyph a theme draws in place of another. Keyed by the name a caller asks for, so a call
 * site says what it means - `arrow-up` - and the theme decides which arrow that is.
 */
export type IconTheme = Partial<Record<IconName, IconName>>;

const IconThemeContext = createContext<IconTheme>({});

export const useIconTheme = () => useContext(IconThemeContext);

/**
 * Swaps glyphs for everything inside it.
 *
 * **The other half of a theme lives in `semantic-ui/`**, where a `@import` inside an app's block
 * re-emits the design system's rules with that app's tokens. A stylesheet can recolor a glyph
 * because every icon fills with `currentColor`, but it cannot choose a different one - which SVG
 * renders is settled in JS - so a theme that wants its own arrow needs this as well.
 *
 * Ship the two halves together: a named set beside the stylesheet that names it, rather than a map
 * assembled at the mount point, or the glyphs and the colors drift apart.
 */
export const IconThemeProvider = ({
  children,
  icons,
}: React.PropsWithChildren<{ icons: IconTheme }>) => (
  <IconThemeContext.Provider value={icons}>
    {children}
  </IconThemeContext.Provider>
);

/**
 * The glyphs spark draws instead of the design system's, paired with `semantic-ui/spark-theme.less`.
 *
 * Kept beside the provider rather than in `configs/`, which every app can reach: a named theme is
 * only meaningful to whatever mounts it, and the two halves are easier to keep honest together.
 */
export const SparkIconTheme: IconTheme = {
  [IconNames.arrowUp]: IconNames.arrowUp2,
  [IconNames.pencil]: IconNames.pencil2,
};
